import { FormEvent, ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { ApiError, api } from "@/api/client";
import { PageHeader } from "@/components/PageHeader";
import { Skeleton } from "@/components/Skeleton";
import { PlatformLogo, platformLabel } from "@/components/PlatformLogo";
import { balanceErrorLabel, formatBalance } from "@/components/PortalBalanceCard";
import { roleLabel } from "@/types";
import type {
  Platform,
  PortalBalance,
  PortalBalances,
  VaultMember,
  VaultPlatformState,
  VaultReveal,
} from "@/types";

/* Vault — team portal-credential management for operations_manager and above.
 * Everyone visible appears with connection status + live balance; the reveal /
 * replace / test / remove actions are available only on rows the viewer may
 * manage (``can_manage``). Mirrors the AdminParsers page shell. */

const PLATFORMS: Platform[] = ["myhome", "ss"];

export function Vault() {
  const [members, setMembers] = useState<VaultMember[] | null>(null);
  const [balances, setBalances] = useState<Record<string, PortalBalances>>({});
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [modal, setModal] = useState<{ member: VaultMember; platform: Platform } | null>(null);

  const load = useCallback(async () => {
    try {
      const rows = await api<VaultMember[]>("/api/vault/members");
      setMembers(rows);
      setErr(null);
      // Balances are best-effort — a slow portal shouldn't block the roster.
      api<PortalBalances[]>("/api/team/portal-balances")
        .then((bs) => {
          const map: Record<string, PortalBalances> = {};
          for (const b of bs) map[b.user_id] = b;
          setBalances(map);
        })
        .catch(() => undefined);
    } catch (e) {
      setErr(
        e instanceof ApiError && e.status === 403
          ? "You don't have access to the Vault."
          : e instanceof Error
            ? e.message
            : "Failed to load.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const filtered = useMemo(() => {
    if (!members) return [];
    const needle = q.trim().toLowerCase();
    if (!needle) return members;
    return members.filter(
      (m) =>
        m.name.toLowerCase().includes(needle) ||
        m.email.toLowerCase().includes(needle) ||
        m.myhome.email?.toLowerCase().includes(needle) ||
        m.ss.email?.toLowerCase().includes(needle),
    );
  }, [members, q]);

  const connectedCount = useMemo(
    () =>
      (members ?? []).filter((m) => m.myhome.connected || m.ss.connected).length,
    [members],
  );

  return (
    <div className="w-full space-y-5">
      <PageHeader
        title="Vault"
        subtitle="Team portal credentials & live balances — manage any member's SS.ge / MyHome login."
        actions={
          members && (
            <div className="flex items-center gap-2 text-[12px] text-slate-500">
              <span className="tabular-nums">{connectedCount}</span> of{" "}
              <span className="tabular-nums">{members.length}</span> connected
            </div>
          )
        }
      />

      {err && (
        <div className="card p-4 text-[13px] text-rose-300">{err}</div>
      )}

      {loading && !err && (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-[76px] w-full rounded-xl" />
          ))}
        </div>
      )}

      {!loading && !err && members && (
        <>
          <input
            className="input max-w-sm"
            placeholder="Search members or account emails…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />

          <div className="space-y-2">
            {filtered.map((m) => (
              <MemberRow
                key={m.user_id}
                member={m}
                balances={balances[m.user_id]}
                onManage={(platform) => setModal({ member: m, platform })}
              />
            ))}
            {filtered.length === 0 && (
              <div className="card p-8 text-center text-[13px] text-slate-500">
                No members match “{q}”.
              </div>
            )}
          </div>
        </>
      )}

      {modal && (
        <VaultCredentialModal
          member={modal.member}
          platform={modal.platform}
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            void load();
          }}
        />
      )}
    </div>
  );
}

function MemberRow({
  member,
  balances,
  onManage,
}: {
  member: VaultMember;
  balances: PortalBalances | undefined;
  onManage: (platform: Platform) => void;
}) {
  return (
    <div className="card px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
      {/* Identity */}
      <div className="flex items-center gap-3 min-w-0 sm:w-56 shrink-0">
        <Avatar url={member.avatar_url} name={member.name} />
        <div className="min-w-0">
          <div className="text-[13px] font-medium text-slate-100 truncate">
            {member.name}
          </div>
          <div className="text-[11px] text-slate-500">{roleLabel(member.role)}</div>
        </div>
      </div>

      {/* Portal cells */}
      <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
        {PLATFORMS.map((p) => (
          <PortalCell
            key={p}
            platform={p}
            state={p === "myhome" ? member.myhome : member.ss}
            balance={balances ? (p === "myhome" ? balances.myhome : balances.ss) : undefined}
            canManage={member.can_manage}
            onManage={() => onManage(p)}
          />
        ))}
      </div>
    </div>
  );
}

function PortalCell({
  platform,
  state,
  balance,
  canManage,
  onManage,
}: {
  platform: Platform;
  state: VaultPlatformState;
  balance: PortalBalance | undefined;
  canManage: boolean;
  onManage: () => void;
}) {
  return (
    <div className="rounded-xl border border-white/5 bg-white/[0.02] px-3 py-2 flex items-center gap-3">
      <PlatformLogo platform={platform} size="sm" />
      <div className="min-w-0 flex-1">
        {state.connected ? (
          <>
            <div className="text-[12px] text-slate-200 font-mono truncate" title={state.email ?? ""}>
              {state.email}
            </div>
            <div className="text-[10.5px] text-slate-500 flex items-center gap-1.5">
              <BalanceTag balance={balance} />
              {platform === "myhome" && (
                <span>· {state.has_phone ? "☎ on file" : "no phone"}</span>
              )}
            </div>
          </>
        ) : (
          <div className="text-[12px] text-slate-500">
            <span className="text-slate-600">○</span> not connected
          </div>
        )}
      </div>
      {canManage ? (
        <button
          type="button"
          onClick={onManage}
          className="btn-secondary !px-2.5 !py-1 !text-[11px] shrink-0"
        >
          {state.connected ? "Manage" : "Connect"}
        </button>
      ) : (
        <span className="text-[10px] text-slate-600 shrink-0" title="You can't manage this member's credentials">
          view-only
        </span>
      )}
    </div>
  );
}

function BalanceTag({ balance }: { balance: PortalBalance | undefined }) {
  if (balance === undefined) return <span className="text-slate-600">balance…</span>;
  if (balance.ok) return <span className="text-emerald-400/90">{formatBalance(balance)}</span>;
  return <span className="text-slate-600">{balanceErrorLabel(balance.error)}</span>;
}

function Avatar({ url, name }: { url: string | null; name: string }) {
  if (url) {
    return (
      <img
        src={url}
        alt=""
        className="h-9 w-9 rounded-full object-cover border border-white/10 shrink-0"
      />
    );
  }
  const initials = name.split(/\s+/).slice(0, 2).map((s) => s[0]).join("").toUpperCase();
  return (
    <div className="h-9 w-9 rounded-full bg-white/[0.05] border border-white/10 flex items-center justify-center text-[11px] font-semibold text-slate-300 shrink-0">
      {initials || "?"}
    </div>
  );
}

/* ── Manage modal: reveal / replace / test / remove for one member+portal ── */

function VaultCredentialModal({
  member,
  platform,
  onClose,
  onSaved,
}: {
  member: VaultMember;
  platform: Platform;
  onClose: () => void;
  onSaved: () => void;
}) {
  const label = platformLabel(platform);
  const requirePhone = platform === "myhome";
  const state = platform === "myhome" ? member.myhome : member.ss;
  const base = `/api/vault/members/${member.user_id}/credentials/${platform}`;

  const [editing, setEditing] = useState(!state.connected);
  const [email, setEmail] = useState(state.email ?? "");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [revealing, setRevealing] = useState(false);
  const [revealed, setRevealed] = useState<VaultReveal | null>(null);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [savedOk, setSavedOk] = useState(false);

  async function reveal() {
    setRevealing(true);
    setErr(null);
    try {
      const r = await api<VaultReveal>(`${base}/reveal`, { method: "POST" });
      setRevealed(r);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Reveal failed");
    } finally {
      setRevealing(false);
    }
  }

  async function save(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setSavedOk(false);
    setTestResult(null);
    setBusy(true);
    try {
      const body: Record<string, unknown> = { email, password };
      if (requirePhone) body.phone = phone;
      await api(base, { method: "PUT", json: body });
      setPassword("");
      setPhone("");
      setSavedOk(true);
      setEditing(false);
      onSaved();
    } catch (error) {
      setErr(error instanceof Error ? error.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  async function runTest() {
    setTesting(true);
    setTestResult(null);
    setErr(null);
    setSavedOk(false);
    try {
      const body: Record<string, unknown> = {};
      if (editing) {
        body.email = email;
        body.password = password;
        if (requirePhone) body.phone = phone;
      }
      const res = await api<{ ok: boolean; code: string; message: string }>(
        `${base}/test`,
        { method: "POST", json: body },
      );
      setTestResult({ ok: res.ok, message: res.message });
    } catch (error) {
      setTestResult({
        ok: false,
        message: error instanceof Error ? error.message : "Test failed",
      });
    } finally {
      setTesting(false);
    }
  }

  async function remove() {
    if (!confirm(`Remove ${member.name}'s ${label} credentials?`)) return;
    await api(base, { method: "DELETE" });
    onSaved();
  }

  const canTestTyped = !!email && !!password && (!requirePhone || !!phone.trim());

  const icon = (
    <div className="h-10 w-10 rounded-xl bg-white/[0.03] border border-white/5 flex items-center justify-center flex-shrink-0">
      <PlatformLogo platform={platform} size="sm" />
    </div>
  );

  return (
    <ModalShell
      icon={icon}
      title={`${member.name} · ${label}`}
      subtitle="Encrypted at rest. Revealing is logged."
      onClose={onClose}
    >
      {state.connected && !editing ? (
        <div className="space-y-4">
          <div className="rounded-xl border border-white/5 bg-white/[0.02] divide-y divide-white/5">
            <Row label="Account">
              <span className="font-mono text-[12.5px] text-slate-200 truncate">
                {state.email}
              </span>
            </Row>
            {requirePhone && (
              <Row label="Phone">
                <span className="text-[12.5px] text-slate-400">
                  {revealed?.phone
                    ? revealed.phone
                    : state.has_phone
                      ? "Saved (hidden)"
                      : "Not set"}
                </span>
              </Row>
            )}
            <Row label="Password">
              {revealed ? (
                <span className="inline-flex items-center gap-2 min-w-0">
                  <span className="font-mono text-[12.5px] text-emerald-300 truncate">
                    {revealed.password}
                  </span>
                  <button
                    type="button"
                    onClick={() => navigator.clipboard?.writeText(revealed.password)}
                    className="text-[11px] text-slate-400 hover:text-slate-200 shrink-0"
                    title="Copy password"
                  >
                    Copy
                  </button>
                </span>
              ) : (
                <button
                  type="button"
                  onClick={reveal}
                  disabled={revealing}
                  className="text-[12px] text-accent-400 hover:text-accent-300"
                >
                  {revealing ? "Revealing…" : "👁 Reveal"}
                </button>
              )}
            </Row>
          </div>

          {revealed && (
            <p className="text-[11px] text-amber-300/80">
              Only you can see this. This reveal was recorded in the audit log.
            </p>
          )}
          {err && <ResultBanner ok={false}>{err}</ResultBanner>}
          {testResult && <ResultBanner ok={testResult.ok}>{testResult.message}</ResultBanner>}

          <div className="flex gap-2">
            <button
              type="button"
              onClick={runTest}
              disabled={testing}
              className="btn-secondary flex-1 !py-2 !text-xs"
            >
              {testing ? "Testing…" : "Test connection"}
            </button>
            <button
              type="button"
              onClick={() => { setEditing(true); setTestResult(null); }}
              className="btn-secondary flex-1 !py-2 !text-xs"
            >
              Replace credentials
            </button>
          </div>
          <button
            type="button"
            onClick={remove}
            className="w-full text-[12px] text-rose-300/80 hover:text-rose-200 py-2 rounded-lg border border-rose-500/20 hover:bg-rose-500/10 transition-colors"
          >
            Remove connection
          </button>
        </div>
      ) : (
        <form onSubmit={save} className="space-y-3">
          <div>
            <label className="label !mb-1">Email</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input !py-2"
              autoFocus
            />
          </div>
          <div>
            <label className="label !mb-1">Password</label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input !py-2"
              placeholder="••••••••"
            />
          </div>
          {requirePhone && (
            <div>
              <label className="label !mb-1">Phone</label>
              <input
                required
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="input !py-2"
                placeholder="5XXXXXXXX"
              />
            </div>
          )}
          {err && <ResultBanner ok={false}>{err}</ResultBanner>}
          {savedOk && <ResultBanner ok>Saved.</ResultBanner>}
          {testResult && <ResultBanner ok={testResult.ok}>{testResult.message}</ResultBanner>}
          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={runTest}
              disabled={!canTestTyped || testing}
              className="btn-secondary !py-2 !text-xs"
            >
              {testing ? "Testing…" : "Test"}
            </button>
            <button type="submit" disabled={busy} className="btn-primary flex-1 !py-2 !text-xs">
              {busy ? "Saving…" : state.connected ? "Save changes" : "Connect account"}
            </button>
          </div>
          {state.connected && (
            <button
              type="button"
              onClick={() => {
                setEditing(false);
                setPassword("");
                setPhone("");
                setErr(null);
                setTestResult(null);
              }}
              className="w-full text-[12px] text-slate-400 hover:text-slate-200 py-1.5"
            >
              Cancel
            </button>
          )}
        </form>
      )}
    </ModalShell>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-2.5">
      <span className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold shrink-0">
        {label}
      </span>
      <span className="min-w-0 text-right">{children}</span>
    </div>
  );
}

function ModalShell({
  title,
  subtitle,
  icon,
  onClose,
  children,
}: {
  title: string;
  subtitle?: string;
  icon?: ReactNode;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/65"
      onClick={onClose}
    >
      <div
        className="card w-full max-w-md overflow-hidden max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 px-5 py-4 border-b border-white/5">
          {icon}
          <div className="min-w-0 flex-1">
            <div className="text-[15px] font-semibold text-slate-100 leading-tight truncate">
              {title}
            </div>
            {subtitle && <div className="text-[12px] text-slate-500 mt-0.5">{subtitle}</div>}
          </div>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-slate-200 text-xl leading-none px-1 -mt-0.5"
            aria-label="close"
          >
            ×
          </button>
        </div>
        <div className="px-5 py-4 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

function ResultBanner({ ok, children }: { ok: boolean; children: ReactNode }) {
  return (
    <div
      className={
        "text-[12px] rounded-lg px-3 py-2 border " +
        (ok
          ? "text-emerald-400 bg-emerald-500/10 border-emerald-500/20"
          : "text-rose-400 bg-rose-500/10 border-rose-500/20")
      }
    >
      {ok ? "✓ " : "✗ "}
      {children}
    </div>
  );
}
